import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LogOut, FileText, Copy, Send, ArrowLeft, Trash2, Search, CheckCircle } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";

const History = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; invoice: any | null }>({ open: false, invoice: null });
  const [paidDialog, setPaidDialog] = useState<{ open: boolean; invoice: any | null }>({ open: false, invoice: null });

  useEffect(() => {
    checkUser();
    fetchInvoices();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/");
    }
  };

  const fetchInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(`
          *,
          clients(name, contact, email, channel_pref),
          files(file_url)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading invoices",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const duplicateInvoice = async (invoice: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("invoices")
        .insert({
          user_id: user.id,
          client_id: invoice.client_id,
          invoice_number: `INV-${Date.now()}`,
          materials: invoice.materials,
          labor_cost: invoice.labor_cost,
          total: invoice.total,
          photo_url: invoice.photo_url,
          status: "draft",
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Invoice duplicated!",
        description: "A copy has been created as a draft.",
      });
      fetchInvoices();
    } catch (error: any) {
      toast({
        title: "Error duplicating invoice",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resendInvoice = async (invoice: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Get the PDF filename from stored URL
      const storedUrl = invoice.files?.[0]?.file_url;
      if (!storedUrl) {
        throw new Error("Invoice PDF not found");
      }

      // Extract filename from the stored URL
      const fileName = storedUrl.includes('/invoices/') 
        ? storedUrl.split('/invoices/')[1].split('?')[0]
        : storedUrl.split('/').slice(-2).join('/');

      // Generate signed URL (valid for 5 minutes)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from("invoices")
        .createSignedUrl(fileName, 300);

      if (signedUrlError) throw signedUrlError;
      const pdfUrl = signedUrlData.signedUrl;

      // Get client's email address
      const clientEmail = invoice.clients?.email;
      const channelPref = invoice.clients?.channel_pref;

      if (!clientEmail) {
        throw new Error("Client email address not found. Please update client information.");
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(clientEmail)) {
        throw new Error("Invalid email address. Please update client contact information.");
      }

      // Send email or telegram based on preference
      if (channelPref === "telegram") {
        // Send via Telegram
        const { error: telegramError } = await supabase.functions.invoke("send-telegram", {
          body: {
            phone_number: invoice.clients?.contact,
            client_name: invoice.clients?.name || "Valued Client",
            invoice_url: pdfUrl,
            total: `RM ${parseFloat(invoice.total).toFixed(2)}`,
          },
        });

        if (telegramError) throw telegramError;
      } else {
        // Send email using edge function
        const { error: emailError } = await supabase.functions.invoke("send-email", {
          body: {
            to_email: clientEmail,
            to_name: invoice.clients?.name || "Valued Client",
            invoice_url: pdfUrl,
            total: `RM ${parseFloat(invoice.total).toFixed(2)}`,
            invoice_id: invoice.id,
          },
        });

        if (emailError) throw emailError;
      }

      // Update status to sent
      await supabase
        .from("invoices")
        .update({ status: "sent" })
        .eq("id", invoice.id);

      toast({
        title: "Invoice resent!",
        description: `Invoice sent to ${invoice.clients?.name}`,
      });
      fetchInvoices();
    } catch (error: any) {
      toast({
        title: "Error resending invoice",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const confirmMarkAsPaid = async () => {
    if (!paidDialog.invoice) return;

    try {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "paid" })
        .eq("id", paidDialog.invoice.id);

      if (error) throw error;

      toast({
        title: "Invoice marked as paid",
        description: `${paidDialog.invoice.invoice_number} has been marked as paid`,
      });
      fetchInvoices();
    } catch (error: any) {
      toast({
        title: "Error updating invoice",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setPaidDialog({ open: false, invoice: null });
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.invoice) return;

    try {
      // Delete associated files from storage
      if (deleteDialog.invoice.files?.[0]?.file_url) {
        const fileName = deleteDialog.invoice.files[0].file_url.split('/').pop();
        if (fileName) {
          await supabase.storage.from("invoices").remove([fileName]);
        }
      }

      // Delete file records
      await supabase.from("files").delete().eq("invoice_id", deleteDialog.invoice.id);

      // Delete invoice
      const { error } = await supabase
        .from("invoices")
        .delete()
        .eq("id", deleteDialog.invoice.id);

      if (error) throw error;

      toast({
        title: "Invoice deleted",
        description: "Invoice has been permanently removed",
      });
      fetchInvoices();
    } catch (error: any) {
      toast({
        title: "Error deleting invoice",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteDialog({ open: false, invoice: null });
    }
  };

  const viewPDF = async (fileUrl: string) => {
    try {
      // Extract the file path from the stored URL
      // URL format: .../storage/v1/object/sign/invoices/USER_ID/TIMESTAMP.pdf?token=...
      let filePath = '';
      
      if (fileUrl.includes('/object/sign/invoices/')) {
        // Extract path after /invoices/
        const afterInvoices = fileUrl.split('/object/sign/invoices/')[1];
        filePath = afterInvoices.split('?')[0]; // Remove query params
      } else if (fileUrl.includes('/invoices/')) {
        filePath = fileUrl.split('/invoices/')[1].split('?')[0];
      } else {
        // Fallback: try to get the last two segments
        filePath = fileUrl.split('/').slice(-2).join('/').split('?')[0];
      }

      // Generate new signed URL (valid for 5 minutes)
      const { data, error } = await supabase.storage
        .from("invoices")
        .createSignedUrl(filePath, 300);

      if (error) throw error;

      window.open(data.signedUrl, "_blank");
    } catch (error: any) {
      toast({
        title: "Error opening PDF",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-success text-success-foreground";
      case "sent":
        return "bg-primary text-primary-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  // Filtered invoices based on search and status
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      // Status filter
      if (statusFilter === "active") {
        if (invoice.status === "paid") return false;
      } else if (statusFilter !== "all" && invoice.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesInvoiceNumber = invoice.invoice_number?.toLowerCase().includes(query);
        const matchesClientName = invoice.clients?.name?.toLowerCase().includes(query);
        const matchesClientContact = invoice.clients?.contact?.toLowerCase().includes(query);
        const matchesClientEmail = invoice.clients?.email?.toLowerCase().includes(query);
        
        // Search in materials
        const matchesMaterials = invoice.materials?.some((material: any) => 
          material.name?.toLowerCase().includes(query)
        );

        return matchesInvoiceNumber || matchesClientName || matchesClientContact || matchesClientEmail || matchesMaterials;
      }

      return true;
    });
  }, [invoices, searchQuery, statusFilter]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="SnapBill" className="h-8 md:h-10 w-8 md:w-10" />
            <div className="hidden sm:block">
              <h1 className="font-display font-bold text-lg md:text-xl">SnapBill</h1>
              <p className="text-xs text-muted-foreground">Snap. Bill. Paid.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="hidden md:flex">
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="md:hidden">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-4 md:py-8 px-4 mb-20 md:mb-8">
        <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Invoice History</h2>
                <p className="text-sm md:text-base text-muted-foreground">View and manage all your invoices</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => navigate("/clients")} className="w-full sm:w-auto">
                  Manage Clients
                </Button>
                <Button onClick={() => navigate("/dashboard")} className="w-full sm:w-auto">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  New Invoice
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by invoice #, client, contact, or items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (Hide Paid)</SelectItem>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Results count */}
            {(searchQuery || statusFilter !== "all") && (
              <p className="text-sm text-muted-foreground">
                Found {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading invoices...
              </CardContent>
            </Card>
          ) : invoices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No invoices yet</p>
                <Button onClick={() => navigate("/dashboard")}>
                  Create your first invoice
                </Button>
              </CardContent>
            </Card>
          ) : filteredInvoices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No invoices match your filters</p>
                <Button variant="outline" onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}>
                  Clear filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredInvoices.map((invoice) => (
                <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1">
                        <CardTitle className="text-lg md:text-xl mb-2">
                          {invoice.invoice_number}
                        </CardTitle>
                        <div className="text-xs md:text-sm text-muted-foreground space-y-1">
                          <p>Client: {invoice.clients?.name || "Unknown"}</p>
                          <p className="break-all">📱 {invoice.clients?.contact || "N/A"}</p>
                          {invoice.clients?.email && (
                            <p className="break-all">✉️ {invoice.clients.email}</p>
                          )}
                          <p>Date: {new Date(invoice.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex sm:flex-col items-start sm:items-end gap-2">
                        <Badge className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                        <p className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">
                          RM {parseFloat(invoice.total).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                   <CardContent className="pt-0">
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => duplicateInvoice(invoice)}
                        className="text-xs"
                      >
                        <Copy className="h-3 w-3 md:h-4 md:w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Duplicate</span>
                      </Button>
                      {invoice.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resendInvoice(invoice)}
                          className="text-xs"
                        >
                          <Send className="h-3 w-3 md:h-4 md:w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Resend</span>
                        </Button>
                      )}
                      {invoice.files?.[0]?.file_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewPDF(invoice.files[0].file_url)}
                          className="text-xs"
                        >
                          <FileText className="h-3 w-3 md:h-4 md:w-4 sm:mr-2" />
                          <span className="hidden sm:inline">View PDF</span>
                        </Button>
                      )}
                      {invoice.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setPaidDialog({ open: true, invoice })}
                          className="text-xs"
                        >
                          <span className="hidden sm:inline">Mark as Paid</span>
                          <span className="sm:hidden">Paid</span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteDialog({ open: true, invoice })}
                        className="text-xs"
                      >
                        <Trash2 className="h-3 w-3 md:h-4 md:w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, invoice: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice <strong>{deleteDialog.invoice?.invoice_number}</strong>?
              <div className="mt-4 space-y-1 text-sm">
                <p>Client: <strong>{deleteDialog.invoice?.clients?.name || 'Unknown'}</strong></p>
                <p>Amount: <strong>RM {deleteDialog.invoice ? parseFloat(deleteDialog.invoice.total).toFixed(2) : '0.00'}</strong></p>
              </div>
              <p className="mt-4 text-destructive font-semibold">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Paid Confirmation Dialog */}
      <AlertDialog open={paidDialog.open} onOpenChange={(open) => !open && setPaidDialog({ open: false, invoice: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Invoice as Paid</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm that payment has been received for invoice <strong>{paidDialog.invoice?.invoice_number}</strong>?
              <div className="mt-4 space-y-1 text-sm">
                <p>Client: <strong>{paidDialog.invoice?.clients?.name || 'Unknown'}</strong></p>
                <p>Amount: <strong>RM {paidDialog.invoice ? parseFloat(paidDialog.invoice.total).toFixed(2) : '0.00'}</strong></p>
              </div>
              <p className="mt-4">This will update the invoice status to "Paid".</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkAsPaid}>
              Confirm Payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default History;
